import React, { useState } from 'react';
import { Users, Plus, Award, Edit2, Star, X } from 'lucide-react';
import { THEMES, getColorClasses } from '../pages/themeConfig';
import { SOLDIER_CLASSES, getNextSkill } from './classes';
import SoldierProfileModal from './SoldierProfileModal';

const SquadRoster = ({ 
  squad, 
  resources, 
  setResources, 
  currentCampaign, 
  apiHandlers, 
  theme = 'vanilla' 
}) => {
  const [editingNicknameId, setEditingNicknameId] = useState(null);
  const [nicknameInput, setNicknameInput] = useState('');
  const [showClassModal, setShowClassModal] = useState(false);
  const [selectedSoldier, setSelectedSoldier] = useState(null);
  const [showProfileModal, setShowProfileModal] = useState(false);
  
  const config = THEMES[theme] || THEMES.vanilla;
  
  // Get the correct rank based on XP (aligned with 9 skill tiers)
  const getRankForXP = (xp) => {
    if (xp >= 1250) return 'Command Sergeant Major';
    if (xp >= 875) return 'Sergeant Major';
    if (xp >= 625) return 'First Sergeant';
    if (xp >= 450) return 'Master Sergeant';
    if (xp >= 300) return 'Sergeant First Class';
    if (xp >= 200) return 'Staff Sergeant';
    if (xp >= 125) return 'Sergeant';
    if (xp >= 60) return 'Corporal';
    if (xp >= 25) return 'Private First Class';
    return 'Private';
  };

  // Auto-promote soldiers based on XP
  React.useEffect(() => {
    squad.forEach(soldier => {
      const xp = soldier.experience || 0;
      const correctRank = getRankForXP(xp);
      
      // If soldier's rank doesn't match their XP, promote them
      if (soldier.rank !== correctRank) {
        apiHandlers.updateSquadMember(soldier.id, {
          rank: correctRank
        }).catch(err => console.error('Failed to auto-promote soldier:', err));
      }
    });
  }, [squad, apiHandlers]);
  
  const getStatusColor = (status) => {
    if (status === 'Ready') return 'text-green-400';
    if (status === 'Wounded') return 'text-red-400';
    if (status === 'Training') return 'text-yellow-400';
    if (status === 'On Mission') return 'text-blue-400';
    return 'text-gray-400';
  };

  const recruitCost = config.squad?.recruitCost || 1000;
  const canAffordRecruit = resources.funds >= recruitCost;

  const handleRecruitSoldier = async () => {
    if (!currentCampaign?.id || !canAffordRecruit) return;

    try {
      const firstNames = ['John', 'Sarah', 'Michael', 'Emma', 'James', 'Lisa', 'Robert', 'Maria', 'David', 'Jennifer', 'William', 'Jessica', 'Richard', 'Ashley', 'Joseph', 'Amanda'];
      const lastNames = ['Anderson', 'Brooks', 'Carter', 'Davis', 'Evans', 'Foster', 'Garcia', 'Harris', 'Johnson', 'Miller', 'Wilson', 'Moore', 'Taylor', 'Thomas', 'Martinez', 'Robinson'];
      
      const randomFirstName = firstNames[Math.floor(Math.random() * firstNames.length)];
      const randomLastName = lastNames[Math.floor(Math.random() * lastNames.length)];
      const fullName = `${randomFirstName} ${randomLastName}`;

      await apiHandlers.createSquadMember(fullName, 'Private', {
        health: 100,
        max_health: 100,
        status: 'Ready',
        missions_completed: 0,
        nickname: null,
        class: null,
        experience: 0,
        unlocked_skills: []
      });

      setResources(prev => ({
        ...prev,
        funds: prev.funds - recruitCost
      }));
    } catch (error) {
      alert('Failed to recruit soldier');
    }
  };

  const handleSetNickname = async (soldier) => {
    if (!currentCampaign?.id || !nicknameInput.trim()) return;

    try {
      await apiHandlers.updateSquadMember(soldier.id, {
        nickname: nicknameInput.trim()
      });
      setEditingNicknameId(null);
      setNicknameInput('');
    } catch (error) {
      alert('Failed to set nickname');
    }
  };

  const handleSpecialize = (soldier) => {
    setSelectedSoldier(soldier);
    setShowClassModal(true);
  };

  const handleChooseClass = async (classId) => {
    if (!selectedSoldier || !currentCampaign?.id) return;

    const soldierClass = SOLDIER_CLASSES[classId];
    if (!soldierClass) return;

    try {
      // Don't reset experience - soldier keeps their XP!
      await apiHandlers.updateSquadMember(selectedSoldier.id, {
        class: classId,
        unlocked_skills: []
      });

      setShowClassModal(false);
      setSelectedSoldier(null);
    } catch (error) {
      alert('Failed to specialize soldier');
    }
  };

  const formatSoldierName = (soldier) => {
    if (soldier.nickname) {
      return `${soldier.name} '${soldier.nickname}'`;
    }
    return soldier.name;
  };

  const getSoldierClass = (soldier) => {
    if (!soldier.class) return null;
    return SOLDIER_CLASSES[soldier.class];
  };

  const getNextSkillInfo = (soldier) => {
    if (!soldier.class || soldier.experience === undefined) return null;
    const unlockedSkills = soldier.unlocked_skills || [];
    return getNextSkill(soldier.class, soldier.experience, unlockedSkills);
  };

  const handleOpenProfile = (soldier) => {
    setSelectedSoldier(soldier);
    setShowProfileModal(true);
  };

  const handleCloseProfile = () => {
    setShowProfileModal(false);
    setSelectedSoldier(null);
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className="text-2xl font-bold">{config.squad?.title || 'Squad Roster'}</h2>
          <p className="text-sm text-gray-400 mt-1">
            {squad.length} {config.squad?.personnelLabel || 'Active Personnel'}
          </p>
        </div>
        <button 
          onClick={handleRecruitSoldier}
          disabled={!canAffordRecruit}
          className={`px-6 py-2 rounded font-bold flex items-center gap-2 transition-all
            ${canAffordRecruit 
              ? `${getColorClasses(config.colors.primary, 'bgMain')} ${getColorClasses(config.colors.primary, 'bgHover')}` 
              : 'bg-gray-600 cursor-not-allowed opacity-50'}`}
        >
          <Users size={20} />
          {config.squad?.recruitButton || 'RECRUIT SOLDIER'} (${recruitCost.toLocaleString()})
        </button>
      </div>

      {squad.length === 0 ? (
        <div className={`bg-gray-800 border-2 border-dashed ${getColorClasses(config.colors.primary, 'border')} rounded-lg p-12 text-center`}>
          <Users className="text-gray-600 mx-auto mb-4" size={64} />
          <h3 className="text-xl font-bold text-gray-400 mb-2">{config.squad?.emptyTitle || 'No Squad Members'}</h3>
          <p className="text-gray-500 mb-6">{config.squad?.emptyMessage || 'Recruit your first soldier to begin building your squad'}</p>
          <button 
            onClick={handleRecruitSoldier}
            disabled={!canAffordRecruit}
            className={`px-8 py-3 rounded-lg font-bold flex items-center gap-2 mx-auto transition-all
              ${canAffordRecruit 
                ? `${getColorClasses(config.colors.primary, 'bgMain')} ${getColorClasses(config.colors.primary, 'bgHover')}` 
                : 'bg-gray-600 cursor-not-allowed opacity-50'}`}
          >
            <Plus size={20} />
            {config.squad?.firstRecruitButton || 'RECRUIT FIRST SOLDIER'}
          </button>
        </div>
      ) : (
        <div className="grid gap-4">
          {squad.map(soldier => {
            const soldierClass = getSoldierClass(soldier);
            const nextSkill = getNextSkillInfo(soldier);
            const unlockedSkillsCount = soldier.unlocked_skills?.length || 0;
            const canSpecialize = soldier.rank === 'Sergeant' && !soldier.class;
            const needsSpecialization = soldier.rank === 'Sergeant' && !soldier.class;

            return (
              <div 
                key={soldier.id} 
                className={`bg-gray-800 border ${getColorClasses(config.colors.primary, 'border')} rounded-lg p-6 hover:border-gray-600 transition-all ${
                  needsSpecialization ? 'ring-2 ring-yellow-500 animate-pulse' : ''
                }`}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className={`${getColorClasses(config.colors.primary, 'bg')} w-16 h-16 rounded-lg flex items-center justify-center`}>
                      {soldierClass ? (
                        <span className="text-3xl">{soldierClass.icon}</span>
                      ) : (
                        <Users className={`${getColorClasses(config.colors.primary, 'text')}`} size={32} />
                      )}
                    </div>
                    <div>
                      {/* Name with Nickname */}
                      <div className="flex items-center gap-2">
                        <h3 className="text-xl font-bold">{formatSoldierName(soldier)}</h3>
                        <button
                          onClick={() => {
                            setEditingNicknameId(soldier.id);
                            setNicknameInput(soldier.nickname || '');
                          }}
                          className="text-gray-400 hover:text-white transition-colors"
                          title="Edit Nickname"
                        >
                          <Edit2 size={14} />
                        </button>
                      </div>

                      {/* Rank and Class */}
                      <div className="flex items-center gap-2">
                        <p className="text-sm text-gray-400">{soldier.rank}</p>
                        {soldierClass && (
                          <p className="text-sm text-purple-400">
                            {soldierClass.icon} {soldierClass.name}
                          </p>
                        )}
                        {needsSpecialization && (
                          <p className="text-sm text-yellow-400 font-bold animate-pulse">
                            ⚠️ MUST SPECIALIZE
                          </p>
                        )}
                      </div>

                      {/* Missions Completed */}
                      <p className="text-xs text-gray-500 mt-1">
                        {config.squad?.missionsLabel || 'Missions Completed'}: {soldier.missions_completed || 0}
                      </p>

                      {/* XP Progress */}
                      {soldier.experience !== undefined && (
                        <div className="mt-2">
                          <div className="flex items-center gap-2">
                            <p className="text-xs text-purple-400">
                              XP: {soldier.experience}
                            </p>
                            {nextSkill && (
                              <p className="text-xs text-gray-500">
                                / {nextSkill.xpRequired} (Next: {nextSkill.name})
                              </p>
                            )}
                          </div>
                          {nextSkill && (
                            <div className="w-48 bg-gray-700 rounded-full h-2 mt-1">
                              <div 
                                className="bg-purple-500 h-2 rounded-full transition-all"
                                style={{ 
                                  width: `${Math.min((soldier.experience / nextSkill.xpRequired) * 100, 100)}%` 
                                }}
                              />
                            </div>
                          )}
                        </div>
                      )}

                      {/* Skills Badge */}
                      {unlockedSkillsCount > 0 && (
                        <div className="flex items-center gap-1 mt-1">
                          <Star size={12} className="text-yellow-400" />
                          <p className="text-xs text-yellow-400">
                            {unlockedSkillsCount} {unlockedSkillsCount === 1 ? 'Skill' : 'Skills'}
                          </p>
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="flex items-center gap-8">
                    <div>
                      <p className="text-xs text-gray-400 mb-1">{config.squad?.healthLabel || 'HEALTH'}</p>
                      <div className="w-32 bg-gray-700 rounded-full h-3">
                        <div 
                          className={`h-3 rounded-full transition-all ${
                            soldier.health >= 75 
                              ? 'bg-green-500' 
                              : soldier.health >= 50 
                              ? 'bg-yellow-500' 
                              : 'bg-red-500'
                          }`}
                          style={{ width: `${(soldier.health / soldier.max_health) * 100}%` }}
                        />
                      </div>
                      <p className="text-xs text-gray-400 mt-1">
                        {soldier.health}/{soldier.max_health}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-400 mb-1">{config.squad?.statusLabel || 'STATUS'}</p>
                      <p className={`font-bold ${getStatusColor(soldier.status)}`}>
                        {soldier.status}
                      </p>
                    </div>
                    <div className="flex flex-col gap-2">
                      {canSpecialize && (
                        <button 
                          onClick={() => handleSpecialize(soldier)}
                          className="bg-purple-600 hover:bg-purple-700 px-4 py-2 rounded font-bold text-sm flex items-center gap-1 animate-pulse"
                        >
                          <Award size={14} />
                          SPECIALIZE NOW!
                        </button>
                      )}
                      <button 
                        onClick={() => handleOpenProfile(soldier)}
                        className={`${getColorClasses(config.colors.secondary, 'bgMain')} ${getColorClasses(config.colors.secondary, 'bgHover')} px-4 py-2 rounded font-bold text-sm`}
                      >
                        {config.squad?.manageButton || 'VIEW PROFILE'}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Squad Summary */}
      {squad.length > 0 && (
        <div className={`mt-6 bg-gray-800 border ${getColorClasses(config.colors.primary, 'border')} rounded-lg p-6`}>
          <h3 className="text-lg font-bold mb-4">{config.squad?.summaryTitle || 'Squad Summary'}</h3>
          <div className="grid grid-cols-4 gap-4">
            <div className="text-center">
              <p className="text-2xl font-bold text-green-400">
                {squad.filter(s => s.status === 'Ready').length}
              </p>
              <p className="text-xs text-gray-400">{config.squad?.statusLabels?.ready || 'Ready'}</p>
            </div>
            <div className="text-center">
              <p className="text-2xl font-bold text-red-400">
                {squad.filter(s => s.status === 'Wounded').length}
              </p>
              <p className="text-xs text-gray-400">{config.squad?.statusLabels?.wounded || 'Wounded'}</p>
            </div>
            <div className="text-center">
              <p className="text-2xl font-bold text-yellow-400">
                {squad.filter(s => s.status === 'Training').length}
              </p>
              <p className="text-xs text-gray-400">{config.squad?.statusLabels?.training || 'Training'}</p>
            </div>
            <div className="text-center">
              <p className="text-2xl font-bold text-blue-400">
                {squad.filter(s => s.status === 'On Mission').length}
              </p>
              <p className="text-xs text-gray-400">{config.squad?.statusLabels?.onMission || 'On Mission'}</p>
            </div>
          </div>
        </div>
      )}

      {/* Nickname Edit Modal */}
      {editingNicknameId && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50">
          <div className="bg-gray-800 border-2 border-purple-500 rounded-lg p-6 w-96">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-bold">Set Nickname</h3>
              <button 
                onClick={() => {
                  setEditingNicknameId(null);
                  setNicknameInput('');
                }}
                className="text-gray-400 hover:text-white"
              >
                <X size={24} />
              </button>
            </div>
            <input
              type="text"
              value={nicknameInput}
              onChange={(e) => setNicknameInput(e.target.value)}
              placeholder="Enter nickname..."
              className="w-full bg-gray-700 border border-gray-600 rounded px-4 py-2 mb-4"
              maxLength={20}
            />
            <div className="flex gap-2">
              <button
                onClick={() => {
                  const soldier = squad.find(s => s.id === editingNicknameId);
                  if (soldier) handleSetNickname(soldier);
                }}
                className="flex-1 bg-purple-600 hover:bg-purple-700 px-4 py-2 rounded font-bold"
              >
                SAVE
              </button>
              <button
                onClick={() => {
                  setEditingNicknameId(null);
                  setNicknameInput('');
                }}
                className="flex-1 bg-gray-600 hover:bg-gray-700 px-4 py-2 rounded font-bold"
              >
                CANCEL
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Class Selection Modal */}
      {showClassModal && selectedSoldier && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50">
          <div className="bg-gray-800 border-2 border-purple-500 rounded-lg p-6 max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-6">
              <div>
                <h3 className="text-2xl font-bold">Choose Specialization</h3>
                <p className="text-sm text-gray-400 mt-1">
                  Select a class for {selectedSoldier.name} - Free at Sergeant rank!
                </p>
              </div>
              <button 
                onClick={() => {
                  setShowClassModal(false);
                  setSelectedSoldier(null);
                }}
                className="text-gray-400 hover:text-white"
              >
                <X size={24} />
              </button>
            </div>

            <div className="grid grid-cols-2 gap-4">
              {Object.values(SOLDIER_CLASSES).map(soldierClass => (
                <div
                  key={soldierClass.id}
                  className="bg-gray-700 border-2 border-gray-600 hover:border-purple-500 rounded-lg p-6 transition-all cursor-pointer"
                  onClick={() => handleChooseClass(soldierClass.id)}
                >
                  <div className="flex items-center gap-3 mb-3">
                    <span className="text-4xl">{soldierClass.icon}</span>
                    <div>
                      <h4 className="text-xl font-bold">{soldierClass.name}</h4>
                      <p className="text-sm text-green-400">FREE</p>
                    </div>
                  </div>
                  <p className="text-sm text-gray-400 mb-4">
                    {soldierClass.description}
                  </p>
                  <div className="space-y-1 text-xs">
                    <p className="text-gray-400">Base Stats:</p>
                    <div className="grid grid-cols-2 gap-1">
                      <p>Health: {soldierClass.baseStats.health}</p>
                      <p>Aim: {soldierClass.baseStats.aim}</p>
                      <p>Mobility: {soldierClass.baseStats.mobility}</p>
                      <p>Defense: {soldierClass.baseStats.defense}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Soldier Profile Modal */}
      {showProfileModal && selectedSoldier && (
        <SoldierProfileModal
          soldier={selectedSoldier}
          onClose={handleCloseProfile}
          config={config}
          theme={theme}
        />
      )}
    </div>
  );
};

export default SquadRoster;